import { useRef, useCallback } from 'react';

/**
 * InfiniteSlider — pure CSS keyframe, no Tailwind, no flicker.
 *
 * Hover behaviour: animation pauses cleanly (industry-standard approach).
 * Changing animation-duration mid-animation causes positional jumps, so we
 * avoid it entirely and use animation-play-state instead.
 *
 * Props:
 *  gap       – gap between items (px), default 16
 *  speed     – full loop duration in seconds, default 25
 *  reverse   – run animation right-to-left instead of left-to-right
 *  pauseOnHover – pause animation when cursor is over the strip (default true)
 *  className – extra class on outer wrapper
 */
export function InfiniteSlider({
  children,
  gap = 16,
  speed = 25,
  reverse = false,
  pauseOnHover = true,
  className = '',
  // legacy prop kept so existing usage doesn't break, just ignored
  speedOnHover,
}) {
  const uid = useRef(`isl-${Math.random().toString(36).slice(2, 8)}`).current;
  const trackRef = useRef(null);

  const pause  = useCallback(() => { if (trackRef.current) trackRef.current.style.animationPlayState = 'paused';  }, []);
  const resume = useCallback(() => { if (trackRef.current) trackRef.current.style.animationPlayState = 'running'; }, []);

  const animDir = reverse ? 'reverse' : 'normal';

  return (
    <>
      <style>{`
        @keyframes ${uid}-scroll {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        .${uid}-track {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: ${gap}px;
          width: max-content;
          will-change: transform;
          animation: ${uid}-scroll ${speed}s linear infinite ${animDir};
        }
      `}</style>

      <div
        style={{ overflow: 'hidden', width: '100%' }}
        className={className}
        onMouseEnter={pauseOnHover ? pause  : undefined}
        onMouseLeave={pauseOnHover ? resume : undefined}
      >
        <div ref={trackRef} className={`${uid}-track`}>
          {children}
          {children}
        </div>
      </div>
    </>
  );
}
